import type { Node as PmNode } from "prosemirror-model";
import { Plugin, PluginKey } from "prosemirror-state";
import type { EditorState, Transaction } from "prosemirror-state";
import type { EditorView } from "prosemirror-view";
import type { Document } from "../types";
import { button } from "./dom";
import { schema } from "./schema";
import { serialize } from "./serialization";

// The user can move selected content into a new card. The server creates the
// card first, and then the selection changes into a section of that card.

type Range = { from: number; to: number };

// The range that waits for its new card. The plugin maps the range through
// each change, so the range stays correct until the card id arrives.
const pendingKey = new PluginKey<Range | null>("extractPending");

export const extractPlugin = new Plugin<Range | null>({
  key: pendingKey,
  state: {
    init: () => null,
    apply(tr, range) {
      const meta = tr.getMeta(pendingKey) as Range | null | undefined;
      if (meta !== undefined) return meta;
      if (!range || !tr.docChanged) return range;
      const from = tr.mapping.map(range.from, 1);
      const to = tr.mapping.map(range.to, -1);
      return from < to ? { from, to } : null;
    },
  },
});

function canExtract(state: EditorState): boolean {
  return !state.selection.empty && pendingKey.getState(state) == null;
}

// Mark the selection as the pending range, and give the content of the new
// card. Returns null when there is no selection, or when a range waits.
export function startExtract(state: EditorState): { tr: Transaction; content: Document } | null {
  if (!canExtract(state)) return null;
  const { from, to, $from, $to } = state.selection;
  // Text in one textblock keeps its textblock, e.g. a heading, but not the
  // list around it. A larger selection keeps all of its parent nodes.
  const content = $from.sameParent($to)
    ? $from.parent.cut($from.parentOffset, $to.parentOffset)
    : state.doc.slice(from, to, true).content;
  const doc = complete(schema.topNodeType.create(null, content));
  return { tr: state.tr.setMeta(pendingKey, { from, to }), content: serialize(doc) };
}

// A cut can remove a node that the schema requires, e.g. the first paragraph of
// a list item. Add the missing nodes again.
function complete(node: PmNode): PmNode {
  if (node.isTextblock || node.isLeaf) return node;
  const children: PmNode[] = [];
  node.forEach((child) => children.push(complete(child)));
  return node.type.createAndFill(node.attrs, children, node.marks) ?? node;
}

// Replace the pending range with a section of the new card.
export function finishExtract(state: EditorState, cardId: string): Transaction | null {
  const range = pendingKey.getState(state);
  if (!range) return null;
  const section = schema.nodes["card_embed"]!.create({ uuid: cardId });
  return state.tr.replaceRangeWith(range.from, range.to, section).setMeta(pendingKey, null);
}

export function cancelExtract(state: EditorState): Transaction {
  return state.tr.setMeta(pendingKey, null);
}

// ── Button ────────────────────────────────────────────────────────────────────
// All editors share one button. Only the editor with the focus shows it.

let extractButton: HTMLButtonElement | null = null;
let press: (() => void) | null = null;

function ensureButton(): HTMLButtonElement {
  if (extractButton) return extractButton;
  extractButton = button("extract-button", () => press?.());
  extractButton.textContent = "⇱ New card";
  extractButton.title = "Move the selection into a new card";
  document.body.appendChild(extractButton);
  return extractButton;
}

// Show the button below the end of the selection of `view`, or hide it when
// the selection cannot be extracted.
export function updateExtractButton(view: EditorView, onPress: () => void): void {
  if (!view.hasFocus() || !canExtract(view.state)) {
    hideExtractButton();
    return;
  }
  const el = ensureButton();
  const coords = view.coordsAtPos(view.state.selection.to);
  el.style.left = `${coords.left + window.scrollX}px`;
  el.style.top = `${coords.bottom + window.scrollY + 6}px`;
  el.style.display = "block";
  press = onPress;
}

export function hideExtractButton(): void {
  if (extractButton) extractButton.style.display = "none";
  press = null;
}
